import { TreeStructureIcon, TrashIcon, PlusIcon } from '@phosphor-icons/react';
import { useProjects } from '@/hooks/use-projects';
import { ReportsService } from '@/api';
import { useQuery } from '@tanstack/react-query';
import { JourneyReport } from '@/components/reports/journey-report';
import { useCallback } from 'react';
import { DatePicker } from '@/components/date-picker';
import { createFileRoute, Link, useRouteContext } from '@tanstack/react-router';
import { reportsOptions } from '@/hooks/query-options';
import type { Report } from '@/types';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { toast } from 'sonner';
import { hasPermission } from '@/lib/permissions';

export const Route = createFileRoute('/_auth/_side-layout/journeys')({
  component: Journeys,
  beforeLoad: async ({ context }) => {
    const projectId = context.projectId;
    if (!projectId) {
      return;
    }
    context.queryClient.prefetchQuery(reportsOptions({ projectId }));
  },
});

function Journeys() {
  const { projectId } = useProjects();
  const { userRole } = useRouteContext({ from: '/_auth' });
  const canEditProject = hasPermission(userRole, { project: ['update'] });
  const reportsQuery = useQuery({
    ...reportsOptions({ projectId: projectId! }),
    enabled: !!projectId,
  });

  const deleteReport = useCallback(
    (reportId: string) => {
      return ReportsService.deleteReport({ reportId })
        .then(() => reportsQuery.refetch())
        .then(() => toast.success('Report Deleted'))
        .catch(() =>
          toast.error('Failed to Delete Report', {
            description: 'Please try again.',
          }),
        );
    },
    [projectId],
  );

  const journeyReports =
    reportsQuery.data?.filter(
      (report: Report) => report.config.type === 'journey',
    ) ?? [];

  return (
    <>
      <PageHeader
        title="Journeys"
        isNew
        description="See the most common ordered paths users take within a session."
        actions={
          canEditProject ? (
            <Button asChild iconLeft={<PlusIcon />}>
              <Link to={'/journeys/add-journey-report' as string}>
                Add Journey Report
              </Link>
            </Button>
          ) : undefined
        }
      />
      <PageContent>
        {journeyReports.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia>
                <TreeStructureIcon className="size-12" />
              </EmptyMedia>
              <EmptyTitle>No Journeys Yet</EmptyTitle>
              <EmptyDescription>
                Create your first journey to explore the most common paths users
                take through your product within a session.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="flex flex-col gap-10">
            <div className="self-start">
              <DatePicker />
            </div>
            {journeyReports.map((report: Report) => (
              <div key={report.id} className="flex flex-col gap-3">
                <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
                  <span>{report.name}</span>
                  {canEditProject && (
                    <TrashIcon
                      className="cursor-pointer"
                      onClick={() => deleteReport(report.id)}
                    />
                  )}
                </h2>
                <div>
                  {report.config.type === 'journey' && (
                    <JourneyReport config={report.config} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </PageContent>
    </>
  );
}
